"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";

interface ServicesSectionProps {
  services: {
    title: string;
    subtitle: string;
    items: Array<{
      title: string;
      imgUrl: string;
      slug?: string;
    }>;
  };
}

const ServicesSection = ({ services }: ServicesSectionProps) => {
  if (!services?.items || services.items.length === 0) return null;

  return (
    <section className="bg-gradient-to-b from-tertiary to-[#0a4384] py-[3rem] md:py-[5rem] px-[1rem] md:px-[2rem] relative overflow-hidden w-full">
      <div className="mx-auto max-w-[120rem] relative z-10 w-full">
        <motion.h2
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="text-[2rem] sm:text-[2.5rem] md:text-[3rem] lg:text-[4rem] font-bold text-white text-center mb-[1rem] px-[1rem]"
        >
          {services?.title}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45, delay: 0.04 }}
          className="text-[1rem] sm:text-[1.2rem] md:text-[1.5rem] text-white/90 text-center mb-[3rem] md:mb-[4rem] px-[1rem]"
        >
          {services?.subtitle}
        </motion.p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[1.5rem] md:gap-[2rem] lg:gap-[3rem]">
          {services.items.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.45,
                delay: Math.min(index * 0.025, 0.15),
              }}
              className="bg-white rounded-[1rem] overflow-hidden shadow-lg hover:shadow-xl transition-shadow w-full"
            >
              <Link
                href={item.slug ? `/services/${item.slug}` : "/services"}
                className="block h-full"
              >
                <div className="relative w-full aspect-square">
                  <Image
                    src={item.imgUrl || ""}
                    alt={item.title}
                    fill
                    className="object-cover"
                  />
                </div>
                <div className="p-[1.5rem] md:p-[2rem]">
                  <h3 className="text-[1.2rem] sm:text-[1.5rem] md:text-[1.8rem] font-bold text-tertiary text-center">
                    {item.title}
                  </h3>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
